import React, { useState, useMemo } from 'react';
import { Coffee, Star, Gift, Crown, Plus, Trophy, Pencil, Trash2, Loader2 } from 'lucide-react';
import NewTrophyModal from '../modals/TrophyModal';
import PrimaryButton from '../ui/PrimaryButton';
import ConfirmDialog from '../ui/ConfirmDialog';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { ShopTrophy } from '@/services/shop/shopTrophies';
import { useTranslation } from 'react-i18next';

// Defined Themes ordered by level
const THEMES = [
    {
        icon: Coffee,
        bgGradient: 'from-[#FFE8D1] to-[#FFF8F0]',
        border: 'border-[#E8DCCB]',
        text: 'text-[#9A5B37]',
        subBg: 'bg-[#FFF8F0]',
        progress: 'bg-[#9A5B37]'
    },
    {
        icon: Star,
        bgGradient: 'from-[#E2E8F0] to-[#F8FAFC]',
        border: 'border-[#E2E8F0]',
        text: 'text-[#475569]',
        subBg: 'bg-[#F8FAFC]',
        progress: 'bg-[#475569]'
    },
    {
        icon: Gift,
        bgGradient: 'from-[#FFF0B3] to-[#FFFCF0]',
        border: 'border-[#F3E7C0]',
        text: 'text-[#B48324]',
        subBg: 'bg-[#FFFCF0]',
        progress: 'bg-[#B48324]'
    },
    {
        icon: Crown,
        bgGradient: 'from-[#DBEAFE] to-[#F0F7FF]',
        border: 'border-[#CFE2FF]',
        text: 'text-[#1E40AF]',
        subBg: 'bg-[#F0F7FF]',
        progress: 'bg-[#1E40AF]'
    }
];

const ShopsTrophies: React.FC = () => {
    const { t } = useTranslation();
    const {
        isAdmin,
        trophies,
        trophiesProgress,
        trophiesLoading,
        addTrophy,
        updateTrophyItem,
        deleteTrophyItem
    } = useAuth();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTrophy, setEditingTrophy] = useState<ShopTrophy | null>(null);

    // Delete Confirmation State
    const [trophyToDelete, setTrophyToDelete] = useState<ShopTrophy | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const totalRedeemed = useMemo(() => {
        if (!trophies.length || !trophiesProgress.length) return 0;

        // Ordiniamo i trofei per valore (anche se lo sono già dal DB, è più sicuro)
        const sortedTrophies = [...trophies].sort((a, b) => a.value - b.value);

        // Accumulatore
        let count = 0;

        // Per ogni cliente nello storico progressi
        trophiesProgress.forEach((clientProgress) => {
            // Contiamo quanti obiettivi ha raggiunto questo singolo cliente
            // Un cliente ha raggiunto un obiettivo se le sue prenotazioni (value) >= valore del trofeo
            const goalsReached = sortedTrophies.filter(
                (trophy) => clientProgress.value >= trophy.value
            ).length;

            count += goalsReached;
        });

        return count;
    }, [trophies, trophiesProgress]);

    // Sort trophies by value (asc)
    const sortedLevels = [...trophies].sort((a, b) => a.value - b.value);

    const handleSave = async (data: { name: string; value: number; prize: string }) => {
        try {
            if (editingTrophy) {
                await updateTrophyItem(editingTrophy.id, data);
                toast.success(t('salon.trophies.toast.updated'));
            } else {
                await addTrophy(data);
                toast.success(t('salon.trophies.toast.created'));
            }
            setIsModalOpen(false);
        } catch (error: any) {
            console.error(error);
            if (error?.code === 'P0001' || error?.message) {
                toast.error(error.message);
            } else {
                toast.error(t('salon.trophies.toast.error'));
            }
        }
    };

    const handleDelete = (trophy: ShopTrophy) => {
        setTrophyToDelete(trophy);
    };

    const handleConfirmDelete = async () => {
        if (!trophyToDelete) return;
        setIsDeleting(true);
        try {
            await deleteTrophyItem(trophyToDelete.id);
            toast.success(t('salon.trophies.toast.deleted'));
            setTrophyToDelete(null);
        } catch (error) {
            console.error(error);
            toast.error(t('salon.trophies.toast.delete_error'));
        } finally {
            setIsDeleting(false);
        }
    };

    const openEditModal = (trophy: ShopTrophy) => {
        setEditingTrophy(trophy);
        setIsModalOpen(true);
    };

    const openNewModal = () => {
        if (!isAdmin || trophies.length >= 4) return;
        setEditingTrophy(null);
        setIsModalOpen(true);
    };

    if (trophiesLoading && trophies.length === 0) {
        return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-gray-400" /></div>;
    }

    return (
        <div className="space-y-8 animate-in fade-in zoom-in duration-300">

            {/* Intro Banner */}
            <div className="bg-slate-900 rounded-3xl p-8 text-white relative overflow-hidden shadow-xl">
                <div className="relative z-10 max-w-2xl">
                    <div className="flex items-center gap-3 mb-3 text-white">
                        <Trophy className="h-6 w-6 text-yellow-400" />
                        <h2 className="text-xl font-bold">{t('salon.trophies.title')}</h2>
                    </div>
                    <p className="text-slate-300 leading-relaxed mb-8">
                        {t('salon.trophies.subtitle')}
                    </p>

                    {/* Statistiche in Glass Box separati */}
                    <div className="flex flex-wrap gap-4">
                        <div className="flex-1 md:flex-none bg-white/10 backdrop-blur-md rounded-2xl p-4 min-w-[130px] border border-white/5">
                            <span className="block text-3xl font-bold text-white mb-1">{trophies.length}</span>
                            <span className="text-[10px] text-slate-300 font-bold tracking-widest uppercase">
                                {t('salon.trophies.stat_participants')}
                            </span>
                        </div>
                        <div className="flex-1 md:flex-none bg-white/10 backdrop-blur-md rounded-2xl p-4 min-w-[130px] border border-white/5">
                            <span className="block text-3xl font-bold text-white mb-1">{totalRedeemed}</span>
                            <span className="text-[10px] text-slate-300 font-bold tracking-widest uppercase">
                                {t('salon.trophies.stat_redeemed')}
                            </span>
                        </div>
                    </div>
                </div>
                {/* Decorative Icon Background */}
                <Trophy className="absolute right-0 bottom-0 text-slate-800 h-64 w-64 -mr-10 -mb-10 opacity-50 rotate-12" />
            </div>

            {/* Header Section */}
            <div className="flex items-center justify-between gap-4">
                <div>
                    <h3 className="text-xl font-bold text-gray-900">{t('salon.trophies.active_levels')}</h3>
                </div>

                {/* Utilizzo PrimaryButton per ombra arancione */}
                {isAdmin && <PrimaryButton
                    label={t('salon.trophies.add_level')}
                    icon={Plus}
                    onClick={openNewModal}
                    disabled={sortedLevels.length >= 4}
                    className={sortedLevels.length >= 4 ? 'opacity-50 cursor-not-allowed' : ''}
                />}
            </div>

            {/* Grid Cards */}
            {sortedLevels.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-2xl">
                    <Trophy size={24} className="mx-auto text-gray-300 mb-2" />
                    <p className="text-gray-400 text-sm">{t('salon.trophies.empty_state')}</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {sortedLevels.map((level, index) => {
                        const theme = THEMES[index] || THEMES[0]; // Fallback to bronze if out of bounds (shouldn't happen with limit 4)
                        const Icon = theme.icon;

                        return (
                            <div
                                key={level.id}
                                className={`group relative p-5 rounded-3xl border bg-gradient-to-br ${theme.bgGradient} ${theme.border} shadow-sm transition-transform hover:-translate-y-1 duration-300`}
                            >
                                {/* Azioni Hover (Top Right) */}
                                {isAdmin && (
                                    <div className="absolute top-5 right-5 flex gap-2">
                                        {/* Bottone Modifica */}
                                        <button
                                            onClick={() => openEditModal(level)}
                                            className={`p-2 rounded-xl ${theme.subBg} ${theme.text} hover:scale-105 transition-transform cursor-pointer`}
                                        >
                                            <Pencil size={20} />
                                        </button>
                                        {/* Bottone Elimina */}
                                        <button
                                            onClick={() => handleDelete(level)}
                                            className={`p-2 rounded-xl ${theme.subBg} ${theme.text} hover:scale-105 transition-transform cursor-pointer`}
                                        >
                                            <Trash2 size={20} />
                                        </button>
                                    </div>
                                )}

                                {/* Header Card (Icona + Titoli) */}
                                <div className="flex items-center gap-4 mb-6">
                                    {/* Background Icona usa subBg */}
                                    <div className={`h-14 w-14 rounded-2xl ${theme.subBg} flex items-center justify-center shadow-sm`}>
                                        <Icon className={theme.text} size={26} />
                                    </div>
                                    <div className="flex flex-col justify-center">
                                        <h4 className={`text-xl font-bold ${theme.text} leading-tight`}>
                                            {level.name}
                                        </h4>
                                        <span className={`text-[12px] font-bold uppercase tracking-widest ${theme.text} opacity-80`}>
                                            {t('salon.trophies.level_prefix')} #{index + 1}
                                        </span>
                                    </div>
                                </div>

                                {/* Box Interno Info (Usa lo stesso subBg dell'icona) */}
                                <div className={`rounded-2xl p-5 ${theme.subBg} shadow-sm space-y-5`}>

                                    {/* Soglia */}
                                    <div className="flex justify-between items-end">
                                        <span className={`text-sm font-semibold ${theme.text} opacity-90`}>
                                            {t('salon.trophies.threshold')}
                                        </span>
                                        <div className="text-right leading-none">
                                            <span className={`text-2xl font-bold ${theme.text}`}>
                                                {level.value}
                                            </span>
                                            <span className={`text-xs font-semibold ml-1 ${theme.text} opacity-70`}>
                                                {t('salon.trophies.bookings')}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Divider */}
                                    <div className={`h-px w-full ${theme.progress} opacity-10`}></div>

                                    {/* Reward */}
                                    <div className="flex items-center gap-2 pt-1">
                                        <Gift size={16} className={`${theme.text}`} />
                                        <span className={`font-semibold text-sm ${theme.text}`}>
                                            {t('salon.trophies.reward_prefix')} <span className="font-bold">{level.prize}</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <NewTrophyModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                initialData={editingTrophy}
                onSubmit={handleSave}
            />

            <ConfirmDialog
                isOpen={!!trophyToDelete}
                title={t('salon.trophies.delete_title')}
                description={t('salon.trophies.delete_desc', { name: trophyToDelete?.name })}
                confirmLabel={t('salon.trophies.delete_confirm')}
                onConfirm={handleConfirmDelete}
                onCancel={() => setTrophyToDelete(null)}
                loading={isDeleting}
            />
        </div>
    );
};

export default ShopsTrophies;